import {
  catchError,
  concatMap,
  debounceTime,
  filter,
  from,
  map,
  Observable,
  of,
  withLatestFrom,
} from 'rxjs';
import { ofType } from 'redux-observable';
import {
  loadVisitsRecordingsSucceeded,
  visitStatusesUpdateRequested,
  visitStatusUpdateFailed,
  visitStatusUpdateSucceeded,
} from './';
import { PayloadAction } from '@reduxjs/toolkit';
import { LoadVisitsRecordingStatPayload, Visit, VisitStatusesUpdatePayload } from './interfaces';
import { NOTE_STATUS } from '@ax/constants';
import { RootState } from '../../';
import { NoteStatusAppointmentInput, updateAppointmentProperties } from '@ax/service';

export const requestUpdateAppointmentStatusesFromTranscriptionStatuses = (
  action$: Observable<any>,
  state$: Observable<RootState>,
) =>
  action$.pipe(
    ofType(loadVisitsRecordingsSucceeded.type),
    filter((action: PayloadAction<LoadVisitsRecordingStatPayload>) => !!action.payload.stats),
    debounceTime(500),
    withLatestFrom(state$),
    concatMap(([action, state]: [PayloadAction<LoadVisitsRecordingStatPayload>, RootState]) => {
      const appointmentStatusesFromTranscription = Object.entries(action.payload.stats)
        .filter(([_, stats]) => stats.length)
        .reduce(
          (statuses, [appointmentId, recordingStats]) => {
            return {
              ...statuses,
              [appointmentId]: recordingStats.every((recordingStat) => recordingStat.noteGenerated)
                ? NOTE_STATUS.NOTE_READY
                : NOTE_STATUS.CONTENT_PROCESSING,
            };
          },
          {} as Record<string, NOTE_STATUS>,
        );

      const visitStatusesFromState = state.visits.items.reduce(
        (statuses: Record<string, NOTE_STATUS>, visit: Visit) => {
          return {
            ...statuses,
            [visit.uuid]: visit.noteStatus as NOTE_STATUS,
          };
        },
        {} as Record<string, NOTE_STATUS>,
      );

      const statusesToUpdate = Object.entries(appointmentStatusesFromTranscription).reduce(
        (statuses, [appointmentId, status]) => {
          const visitStatus = visitStatusesFromState[appointmentId];

          if (visitStatus !== status && visitStatus <= 3) {
            return {
              ...statuses,
              [appointmentId]: status,
            };
          }

          return statuses;
        },
        {} as Record<string, NOTE_STATUS>,
      );

      const actions = [];

      if (Object.keys(statusesToUpdate).length) {
        actions.push(visitStatusesUpdateRequested({ statuses: statusesToUpdate }));
      }

      return actions;
    }),
  );

export const updateAppointmentStatuses = (action$: Observable<any>) =>
  action$.pipe(
    ofType(visitStatusesUpdateRequested.type),
    concatMap((action: PayloadAction<VisitStatusesUpdatePayload>) => {
      const noteStatusAppointmentInputs: NoteStatusAppointmentInput[] = Object.entries(
        action.payload.statuses,
      ).map(([noteId, noteStatus]) => {
        return {
          appointmentId: noteId,
          noteId,
          noteStatus,
        };
      });

      return from(noteStatusAppointmentInputs).pipe(
        concatMap((noteStatusAppointmentInput) => {
          return from(updateAppointmentProperties(noteStatusAppointmentInput)).pipe(
            map(() =>
              visitStatusUpdateSucceeded({
                visitId: noteStatusAppointmentInput.appointmentId,
                status: noteStatusAppointmentInput.noteStatus,
              }),
            ),
            catchError(() =>
              of(
                visitStatusUpdateFailed({
                  visitId: noteStatusAppointmentInput.appointmentId,
                  status: noteStatusAppointmentInput.noteStatus,
                }),
              ),
            ),
          );
        }),
      );
    }),
  );

export default [
  requestUpdateAppointmentStatusesFromTranscriptionStatuses,
  updateAppointmentStatuses,
];
